import { SupportedTools } from '../../interfaces'

// Maps internal field names (like "process_name") to source-specific names (like "FileName")
export type ToolFieldMap = {
  [internalField: string]: string
}

// This is now a fully typed mapping
export type ToolFieldMappings = Partial<Record<SupportedTools, ToolFieldMap>>

// Usage:
export const renderToolFieldMappings: ToolFieldMappings = {
  'CarbonBlack Cloud - Investigate': {
    dns_query: 'netconn_domain',
    domain: 'netconn_domain',
    src_ip: 'netconn_ipv4',
    dest_ip: 'netconn_ipv4',
    hostname: 'device_name',
    object_loaded: 'modload_name',
    parent_process_cmdline: 'parent_cmdline',
    parent_process_name: 'parent_name',
    parent_process_path: 'parent_name',
    process_cmdline: 'process_cmdline',
    process_name: 'process_name',
    process_path: 'process_name',
    registry_path: 'regmod_name',
    target_path: 'filemod_name',
    username: 'username',
  },
  'CarbonBlack Response': {
    domain: 'domain',
    dns_query: 'domain',
    dns_answer: 'ipaddr',
    hostname: 'hostname',
    object_loaded: 'modload',
    parent_process_name: 'parent_name',
    parent_process_path: 'parent_name',
    process_cmdline: 'cmdline',
    process_name: 'process_name',
    process_path: 'path',
    registry_path: 'regmod',
    src_ip: 'ipaddr',
    dest_ip: 'ipaddr',
    target_path: 'filemod',
    username: 'username',
  },
  CrowdStrike: {
    action: 'Status',
    dest_ip: 'RemoteAddressIP4',
    dest_port: 'RPort',
    dns_answer: 'FirstIP4Record',
    dns_query: 'DomainName',
    domain: 'DomainName',
    event_type: 'event_simpleName',
    hostname: 'ComputerName',
    message: '_raw',
    object_loaded: 'ImageFileName',
    object_name: 'TargetFileName',
    parent_process_id: 'ParentProcessId_decimal',
    parent_process_name: 'ParentBaseFileName',
    parent_process_path: 'ParentBaseFileName',
    process_cmdline: 'CommandLine',
    process_id: 'RawProcessId_decimal',
    process_name: 'FileName',
    process_path: 'ImageFileName',
    protocol: 'Protocol',
    referrer: 'ReferrerUrl',
    registry_key_name: 'RegValueName',
    registry_path: 'RegObjectName',
    registry_value_name: 'RegStringValue',
    service_file_path: 'ServiceImagePath',
    service_name: 'ServiceDisplayName',
    src_ip: 'LocalAddressIP4',
    target_path: 'TargetFileName',
    url: 'HttpUrl',
    user_agent: 'ClientUserAgentString',
    username: 'UserName',
  },
  'CrowdStrike LogScale': {
    action: 'Status',
    dest_hostname: 'TargetServerName',
    dest_ip: 'RemoteAddressIP4',
    dest_port: 'RemotePort',
    details: 'event_data',
    dns_answer: 'FirstIP4Record',
    dns_query: 'DomainName',
    domain: 'DomainName',
    event_platform: 'event_platform',
    event_provider: '#event.dataset',
    event_type: '#event_simpleName',
    file_name: 'FileName',
    file_path: 'TargetFileName',
    hostname: 'ComputerName',
    logon_process: 'LogonProcess',
    logon_type: 'LogonType',
    md5: 'MD5HashData', // Changed in latest release of LogScale
    message: '_raw',
    object_loaded: 'ImageFileName',
    object_name: 'TargetFileName',
    parent_process_id: 'ParentProcessId',
    parent_process_name: 'ParentBaseFileName',
    parent_process_path: 'ParentBaseFileName',
    process_cmdline: 'CommandLine',
    process_id: 'RawProcessId_decimal',
    process_name: 'FileName',
    process_path: 'ImageFileName',
    protocol: 'Protocol',
    record_id: 'EventRecordId',
    referrer: 'ReferrerUrl',
    registry_key_name: 'RegValueName',
    registry_new_value_name: 'RegStringValue',
    registry_path: 'RegObjectName',
    registry_value_name: 'RegStringValue',
    repo: '#repo',
    repo_subtype: '#event.module',
    service_file_path: 'ServiceImagePath',
    service_name: 'ServiceDisplayName',
    sha256: 'SHA256HashData', // Changed in latest release of LogScale
    sid: 'UserSid',
    src_ip: 'LocalAddressIP4',
    src_port: 'LocalPort',
    target_path: 'TargetFileName',
    url: 'HttpUrl',
    user_agent: 'ClientUserAgentString',
    username: 'UserName',
  },
  // NOTE: These are a 1:1 map, however, they need to be wrapped in "{{!=" and "!}}", as they will go through a separate engine written in Eta markup to convert the markup to strings
  Elastic: {
    access: 'access',
    action: 'action',
    dest_ip: 'dest_ip',
    dest_port: 'dest_port',
    dns_answer: 'dns_answer',
    dns_query: 'dns_query',
    domain: 'domain',
    event_id: 'event_id',
    event_type: 'event_type',
    hostname: 'hostname',
    message: 'message',
    object_loaded: 'object_loaded',
    object_name: 'object_loaded',
    parent_process_cmdline: 'parent_process_cmdline',
    parent_process_id: 'parent_process_id',
    parent_process_name: 'parent_process_name',
    parent_process_path: 'parent_process_path',
    pipe_name: 'pipe_name',
    process_cmdline: 'process_cmdline',
    process_id: 'process_id',
    process_name: 'process_name',
    process_path: 'process_path',
    protocol: 'protocol',
    referrer: 'referrer',
    registry_key_name: 'registry_key_name',
    registry_path: 'registry_path',
    registry_value_name: 'registry_value_name',
    service: 'service',
    service_file_path: 'service_file_path',
    service_name: 'service_name',
    src_ip: 'src_ip',
    target_path: 'target_path',
    task_category: 'task_category',
    url: 'url',
    user_agent: 'user_agent',
    username: 'username',
  },
  // NOTE: These are a 1:1 map, however, they need to be wrapped in "{{!=" and "!}}", as they will go through a separate engine written in Eta markup to convert the markup to strings
  'Google SecOps': {
    access: 'access',
    action: 'action',
    dest_ip: 'dest_ip',
    dest_port: 'dest_port',
    dns_answer: 'dns_answer',
    dns_query: 'dns_query',
    domain: 'domain',
    event_id: 'event_id',
    event_type: 'event_type',
    hostname: 'hostname',
    md5: 'md5',
    message: 'message',
    object_loaded: 'object_loaded',
    object_name: 'object_loaded',
    parent_process_cmdline: 'parent_process_cmdline',
    parent_process_id: 'parent_process_id',
    parent_process_name: 'parent_process_name',
    parent_process_path: 'parent_process_path',
    pipe_name: 'pipe_name',
    process_cmdline: 'process_cmdline',
    process_id: 'process_id',
    process_name: 'process_name',
    process_path: 'process_path',
    protocol: 'protocol',
    referrer: 'referrer',
    registry_key_name: 'registry_key_name',
    registry_path: 'registry_path',
    registry_value_name: 'registry_value_name',
    sha256: 'sha256',
    service: 'service',
    service_file_path: 'service_file_path',
    service_name: 'service_name',
    src_ip: 'src_ip',
    target_path: 'target_path',
    task_category: 'task_category',
    url: 'url',
    user_agent: 'user_agent',
    username: 'username',
  },
  'Microsoft Defender': {
    dest_ip: 'RemoteIP',
    dest_port: 'RemotePort',
    domain: 'RemoteUrl',
    event_type: 'ActionType',
    hostname: 'DeviceName',
    md5: 'MD5',
    object_loaded: 'FolderPath',
    parent_process_cmdline: 'InitiatingProcessCommandLine',
    parent_process_id: 'InitiatingProcessId',
    parent_process_name: 'InitiatingProcessFileName',
    parent_process_path: 'InitiatingProcessFolderPath',
    pipe_name: 'PipeName',
    process_cmdline: 'ProcessCommandLine',
    process_id: 'ProcessId',
    process_name: 'FileName',
    process_path: 'FolderPath',
    protocol: 'Protocol',
    registry_key_name: 'RegistryValueName',
    registry_path: 'RegistryKey',
    registry_value_name: 'RegistryValueData',
    service_name: 'service',
    sha256: 'SHA256',
    src_ip: 'LocalIP',
    src_port: 'LocalPort',
    target_path: 'FolderPath',
    url: 'RemoteUrl',
    username: 'AccountName',
  },
  // NOTE: These are a 1:1 map, however, they need to be wrapped in "{{!=" and "!}}", as they will go through a separate engine written in Eta markup to convert the markup to strings
  'Microsoft Sentinel': {
    access: 'access',
    action: 'action',
    dest_ip: 'dest_ip',
    dest_port: 'dest_port',
    dns_answer: 'dns_answer',
    dns_query: 'dns_query',
    domain: 'domain',
    event_id: 'event_id',
    event_type: 'event_type',
    hostname: 'hostname',
    md5: 'md5',
    message: 'message',
    object_loaded: 'object_loaded',
    object_name: 'object_loaded',
    parent_process_cmdline: 'parent_process_cmdline',
    parent_process_id: 'parent_process_id',
    parent_process_name: 'parent_process_name',
    parent_process_path: 'parent_process_path',
    pipe_name: 'pipe_name',
    process_cmdline: 'process_cmdline',
    process_id: 'process_id',
    process_name: 'process_name',
    process_path: 'process_path',
    protocol: 'protocol',
    referrer: 'referrer',
    registry_key_name: 'registry_key_name',
    registry_path: 'registry_path',
    registry_value_name: 'registry_value_name',
    service: 'service',
    service_file_path: 'service_file_path',
    service_name: 'service_name',
    sha256: 'sha256',
    src_ip: 'src_ip',
    target_path: 'target_path',
    task_category: 'task_category',
    url: 'url',
    user_agent: 'user_agent',
    username: 'username',
  },
  'Palo Alto Cortex XDR': {
    dest_ip: 'action_remote_ip',
    dest_port: 'action_remote_port',
    dns_answer: 'dns_resolutions',
    dns_query: 'dns_query_name',
    domain: 'http_server',
    event_id: 'action_evtlog_event_id',
    event_type: 'event_type',
    hostname: 'agent_hostname',
    message: 'action_evtlog_message',
    object_loaded: 'action_module_path',
    object_name: 'action_file_path',
    parent_process_cmdline: 'os_actor_process_command_line',
    parent_process_id: 'os_actor_process_os_pid',
    parent_process_name: 'os_actor_process_image_name',
    parent_process_path: 'os_actor_process_image_path',
    pipe_name: 'actor_remote_pipe_name',
    process_cmdline: 'action_process_image_command_line',
    process_id: 'action_process_os_pid',
    process_name: 'action_process_image_name',
    process_path: 'action_process_image_path',
    protocol: 'action_network_protocol',
    referrer: 'http_referer',
    registry_key_name: 'action_registry_value_name',
    registry_path: 'action_registry_key_name',
    registry_value_name: 'action_registry_data',
    src_ip: 'action_local_ip',
    src_port: 'action_local_port',
    target_path: 'action_file_path',
    user_agent: 'action_user_agent',
    username: 'actor_primary_username',
    sha256: 'action_file_sha256',
    md5: 'action_file_md5',
  },
  'QRadar Query': {
    access: 'access',
    action: 'action',
    dest_ip: 'destIp',
    dest_port: 'destPort',
    dns_answer: 'dnsAnswer',
    dns_query: 'dnsQuery',
    domain: 'domain',
    event_id: 'eventId',
    event_type: 'eventType',
    hostname: 'hostname',
    md5: 'md5',
    message: 'message',
    object_loaded: 'objectLoaded',
    object_name: 'objectLoaded',
    parent_process_cmdline: 'parentProcessCmdline',
    parent_process_id: 'parentProcessId',
    parent_process_name: 'parentProcessName',
    parent_process_path: 'parentProcessPath',
    pipe_name: 'pipeName',
    process_cmdline: 'processCmdline',
    process_id: 'processId',
    process_name: 'processName',
    process_path: 'processPath',
    protocol: 'protocol',
    referrer: 'referrer',
    registry_key_name: 'registryKeyName',
    registry_path: 'registryPath',
    registry_value_name: 'registryValueName',
    service: 'service',
    service_file_path: 'serviceFilePath',
    service_name: 'serviceName',
    sha256: 'sha256',
    src_ip: 'srcIp',
    target_path: 'targetPath',
    task_category: 'taskCategory',
    url: 'url',
    user_agent: 'userAgent',
    username: 'username',
  },
  SentinelOne: {
    dest_ip: 'DstIP',
    dest_port: 'DstPort',
    dns_request: 'DnsRequest',
    dns_answer: 'DnsResponse',
    domain: 'DnsRequest',
    event_type: 'ObjectType',
    hostname: 'EndpointName',
    md5: 'Md5',
    object_loaded: 'ModulePath',
    object_name: 'RegistryKeyPath',
    parent_process_cmdline: 'SrcProcCmdLine',
    parent_process_id: 'SrcProcPID',
    parent_process_name: 'SrcProcName',
    parent_process_path: 'SrcProcImagePath',
    pipe_name: 'NamedPipeName',
    process_cmdline: 'TgtProcCmdLine',
    process_id: 'TgtProcPID',
    process_name: 'TgtProcName',
    process_path: 'TgtProcImagePath',
    protocol: 'NetProtocolName',
    registry_key_name: 'RegistryKeyPath',
    registry_path: 'RegistryKeyPath',
    registry_value_name: 'RegistryValue',
    sha256: 'Sha256',
    src_ip: 'SrcIP',
    target_path: 'TgtFilePath',
    url: 'Url',
    username: 'UserName',
  },
  'SentinelOne - PowerQuery': {
    access: 'tgt.process.accessRights',
    dest_ip: 'dst.ip.address',
    dest_port: 'dst.port.number',
    dns_answer: 'event.dns.response',
    dns_query: 'event.dns.request',
    domain: 'event.dns.request',
    event_id: 'winEventLog.id',
    event_type: 'event.type',
    hostname: 'endpoint.name',
    md5: 'tgt.file.md5',
    message: 'cmdscript.content',
    object_loaded: 'module.path',
    object_name: 'registry.keyPath',
    parent_process_cmdline: 'src.process.cmdline',
    parent_process_id: 'src.process.pid',
    parent_process_name: 'src.process.name',
    parent_process_path: 'src.process.image.path',
    pipe_name: 'namedPipe.name',
    process_cmdline: 'tgt.process.cmdline',
    process_id: 'tgt.process.pid',
    process_name: 'tgt.process.name',
    process_path: 'tgt.process.image.path',
    protocol: 'event.network.protocolName',
    referrer: 'http_request.referrer',
    registry_key_name: 'registry.keyPath',
    registry_path: 'registry.keyPath',
    registry_value_name: 'registry.value',
    service: 'win_service.name',
    service_file_path: 'win_service.cmd_line',
    service_name: 'win_service.name',
    sha256: 'tgt.file.sha256',
    src_ip: 'src.ip.address',
    target_path: 'tgt.file.path',
    url: 'url.address',
    user_agent: 'http_request.user_agent',
    username: 'src.process.user',
  },
  'SentinelOne Singularity': {
    access: 'tgt.process.accessRights',
    dest_ip: 'dst.ip.address',
    dest_port: 'dst.port.number',
    dns_answer: 'event.dns.response',
    dns_query: 'event.dns.request',
    domain: 'event.dns.request',
    event_id: 'winEventLog.id',
    event_type: 'event.type',
    hostname: 'endpoint.name',
    md5: 'tgt.file.md5',
    message: 'cmdscript.content',
    object_loaded: 'module.path',
    object_name: 'registry.keyPath',
    parent_process_cmdline: 'src.process.cmdline',
    parent_process_id: 'src.process.pid',
    parent_process_name: 'src.process.name',
    parent_process_path: 'src.process.image.path',
    pipe_name: 'namedPipe.name',
    process_cmdline: 'tgt.process.cmdline',
    process_id: 'tgt.process.pid',
    process_name: 'tgt.process.name',
    process_path: 'tgt.process.image.path',
    protocol: 'event.network.protocolName',
    referrer: 'http_request.referrer',
    registry_key_name: 'registry.keyPath',
    registry_path: 'registry.keyPath',
    registry_value_name: 'registry.value',
    service: 'win_service.name',
    service_file_path: 'win_service.cmd_line',
    service_name: 'win_service.name',
    sha256: 'tgt.file.sha256',
    src_ip: 'src.ip.address',
    target_path: 'tgt.file.path',
    url: 'url.address',
    user_agent: 'http_request.user_agent',
    username: 'src.process.user',
  },
  // NOTE: These are a 1:1 map, however, they need to be wrapped in "{{!=" and "!}}", as they will go through a separate engine written in Eta markup to convert the markup to strings
  Splunk: {
    access: 'access',
    action: 'action',
    dest_ip: 'dest_ip',
    dest_port: 'dest_port',
    dns_answer: 'dns_answer',
    dns_query: 'dns_query',
    domain: 'domain',
    event_id: 'event_id',
    event_type: 'event_type',
    hostname: 'hostname',
    message: 'message',
    object_loaded: 'object_loaded',
    object_name: 'object_loaded',
    parent_process_cmdline: 'parent_process_cmdline',
    parent_process_id: 'parent_process_id',
    parent_process_name: 'parent_process_name',
    parent_process_path: 'parent_process_path',
    pipe_name: 'pipe_name',
    process_cmdline: 'process_cmdline',
    process_id: 'process_id',
    process_name: 'process_name',
    process_path: 'process_path',
    protocol: 'protocol',
    referrer: 'referrer',
    registry_key_name: 'registry_key_name',
    registry_path: 'registry_path',
    registry_value_name: 'registry_value_name',
    service: 'service',
    service_file_path: 'service_file_path',
    service_name: 'service_name',
    src_ip: 'src_ip',
    target_path: 'target_path',
    task_category: 'task_category',
    url: 'url',
    user_agent: 'user_agent',
    username: 'username',
  },
  'Trend Micro Vision One': {
    dest_ip: 'rawDstIp',
    dest_port: 'rawDstPort',
    dns_query: 'hostName',
    dns_answer: 'objectIps',
    domain: 'domainName',
    event_id: 'eventId',
    event_type: 'eventSubId',
    hostname: 'endpointHostName',
    md5: 'objectFileHashMd5',
    object_loaded: 'objectFilePath',
    object_name: 'objectFilePath',
    parent_process_cmdline: 'parentCmd',
    parent_process_id: 'parentPid',
    parent_process_name: 'parentFileName',
    parent_process_path: 'parentFilePath',
    process_cmdline: 'processCmd',
    process_id: 'processPid',
    process_name: 'processName',
    process_path: 'processFilePath',
    registry_key_name: 'objectRegistryValue',
    registry_path: 'objectRegistryKeyHandle',
    registry_value_name: 'objectRegistryData',
    src_ip: 'rawSrcIp',
    sha256: 'objectFileHashSha256',
    target_path: 'objectFilePath',
    url: 'request',
    username: 'logonUser',
  },
}

/**
 * Per-tool, per-category field overrides.
 *
 * Some tools use different field names depending on the event category.
 * For example, CrowdStrike LogScale uses Context* fields (the acting process)
 * in file_event/registry_event/image_load/dns categories, but uses the default
 * fields (the spawned process) for process_creation.
 */
export type CategoryFieldOverrides = Partial<
  Record<SupportedTools, Record<string, Record<string, string>>>
>

export const categoryFieldOverrides: CategoryFieldOverrides = {
  // added missing categories (process_creation, network_connection, image_load, system, security, authentication_events, pipe_created) -- bkd 03292026
  'CrowdStrike LogScale': {
    // process_creation: the spawned process uses TargetProcessId for its PID -- typically this will be mapped to the RawProcessId_decimal field, but we need to override the mapping for firstSeen/lastSeen aggregations to use the TargetProcessId field since that's what LogScale uses for min/max timestamp grouping in process_creation events
    process_creation: {
      process_id: 'TargetProcessId',
    },
    // file_event: the process writing/creating the file are the Context fields
    file_event: {
      process_id: 'ContextProcessId',
      process_name: 'ContextBaseFileName',
      process_path: 'ContextImageFileName',
    },
    // network_connection: NetworkConnectIP4 doesn't carry ContextImageFileName
    // (that's file_event-only); fall back to the basename for both name + path.
    network_connection: {
      process_id: 'ContextProcessId',
      process_name: 'ContextBaseFileName',
      process_path: 'ContextImageFileName',
    },
    // registry_event: the process modifying the registry key are the Context fields
    registry_event: {
      process_id: 'ContextProcessId',
      process_name: 'FileName',
      process_path: 'ImageFileName',
      sha256: 'TargetSHA256HashData',
    },
    // added missing categories and associated field overrides -- bkd 03292026
    image_load: {
      process_id: 'TargetProcessId',
      process_name: 'TargetImageFileName',
      process_path: 'TargetImageFileName',
    },
    // dns: the process resolving the DNS query are the Context fields
    dns: {
      process_id: 'ContextProcessId',
      process_name: 'ContextBaseFileName',
      process_path: 'ContextBaseFileName',
      dest_ip: 'RespondingDnsServer',
      
    },
    // added missing categories and associated field overrides -- bkd 03292026
    system: {
      process_id: 'ContextProcessId',
      process_name: 'ContextBaseFileName',
      process_path: 'ContextImageFileName',
    },
    // added missing categories and associated field overrides -- bkd 03292026
    security: {
      process_id: 'RpcClientProcessId',
      process_cmdline: 'TaskExecArguments',
      process_path: 'TaskExecCommand',
    },
    // added missing categories and associated field overrides -- bkd 03292026
    authentication_events: {
      process_id: 'ContextProcessId',
    },
    // pipe_created: NamedPipe events use ImageFileName for the acting process,
    // not Context* (those are file_event-scoped).
    pipe_created: {
      process_id: 'ContextProcessId',
      process_name: 'ImageFileName',
      process_path: 'ImageFileName',
    }
  },
  // Copy verbatim from LogScale
  'CrowdStrike': {
    process_creation: {
      process_id: 'TargetProcessId',
    },
    file_event: {
      process_id: 'ContextProcessId',
      process_name: 'ContextBaseFileName',
      process_path: 'ContextImageFileName',
    },
    network_connection: {
      process_id: 'ContextProcessId',
      process_name: 'ContextBaseFileName',
      process_path: 'ContextImageFileName',
    },
    registry_event: {
      process_id: 'ContextProcessId',
      process_name: 'FileName',
      process_path: 'ImageFileName',
      sha256: 'TargetSHA256HashData',
    },
    image_load: {
      process_id: 'TargetProcessId',
      process_name: 'TargetImageFileName',
      process_path: 'TargetImageFileName',
    },
    dns: {
      process_id: 'ContextProcessId',
      process_name: 'ContextBaseFileName',
      process_path: 'ContextBaseFileName',
      dest_ip: 'RespondingDnsServer',
      
    },
    system: {
      process_id: 'ContextProcessId',
      process_name: 'ContextBaseFileName',
      process_path: 'ContextImageFileName',
    },
    security: {
      process_id: 'RpcClientProcessId',
      process_cmdline: 'TaskExecArguments',
      process_path: 'TaskExecCommand',
    },
    authentication_events: {
      process_id: 'ContextProcessId',
    },
    // pipe_created: NamedPipe events use ImageFileName for the acting process,
    // not Context* (those are file_event-scoped).
    pipe_created: {
      process_id: 'ContextProcessId',
      process_name: 'ImageFileName',
      process_path: 'ImageFileName',
    }
  },
  'Palo Alto Cortex XDR': {
    process_creation: {
      md5: 'action_process_image_md5',
      sha256: 'action_process_image_sha256',
      username: 'action_process_username',
    },
    file_event: {
      process_cmdline: 'actor_process_command_line',
      process_id: 'actor_process_os_pid',
      process_name: 'actor_process_image_name',
      process_path: 'actor_process_image_path',
    },
    registry_event: {
      md5: 'actor_process_image_md5',
      sha256: 'actor_process_image_sha256',
      process_cmdline: 'actor_process_command_line',
      process_id: 'actor_process_os_pid',
      process_name: 'actor_process_image_name',
      process_path: 'actor_process_image_path',
    },
    image_load: {
      md5: 'action_module_md5',
      sha256: 'action_module_sha256',
      process_cmdline: 'actor_process_command_line',
      process_id: 'actor_process_os_pid',
      process_name: 'actor_process_image_name',
      process_path: 'actor_process_image_path',
    },
    network_connection: {
      md5: 'actor_process_image_md5',
      sha256: 'actor_process_image_sha256',
      process_cmdline: 'actor_process_command_line',
      process_id: 'actor_process_os_pid',
      process_name: 'actor_process_image_name',
      process_path: 'actor_process_image_path',
    },
    dns: {
      process_cmdline: 'actor_process_command_line',
      process_id: 'actor_process_os_pid',
      process_name: 'actor_process_image_name',
      process_path: 'actor_process_image_path',
    },
  },
  'Microsoft Defender': {
    file_event: {
      process_cmdline: 'InitiatingProcessCommandLine',
      process_id: 'InitiatingProcessId',
      process_name: 'InitiatingProcessFileName',
      process_path: 'InitiatingProcessFolderPath',
    },
    registry_event: {
      process_cmdline: 'InitiatingProcessCommandLine',
      process_id: 'InitiatingProcessId',
      process_name: 'InitiatingProcessFileName',
      process_path: 'InitiatingProcessFolderPath',
    },
    network_connection: {
      process_cmdline: 'InitiatingProcessCommandLine',
      process_id: 'InitiatingProcessId',
      process_name: 'InitiatingProcessFileName',
      process_path: 'InitiatingProcessFolderPath',
    },
    image_load: {
      process_cmdline: 'InitiatingProcessCommandLine',
      process_id: 'InitiatingProcessId',
      process_name: 'InitiatingProcessFileName',
      process_path: 'InitiatingProcessFolderPath',
    },
    pipe_created: {
      process_cmdline: 'InitiatingProcessCommandLine',
      process_id: 'InitiatingProcessId',
      process_name: 'InitiatingProcessFileName',
      process_path: 'InitiatingProcessFolderPath',
      pipe_name: 'tostring(parse_json(AdditionalFields).PipeName)',
    },

    dns: {
      dns_query: 'tostring(parse_json(AdditionalFields).query)',
      dns_answer: 'tostring(parse_json(AdditionalFields).answers)',
      process_cmdline: 'InitiatingProcessCommandLine',
      process_id: 'InitiatingProcessId',
      process_name: 'InitiatingProcessFileName',
      process_path: 'InitiatingProcessFolderPath',
    },
  },
  'SentinelOne': {
    process_creation: {
      //sha256: 'TgtProcImageSha256',
      //md5: 'TgtProcImageMd5',
    },
    file_event: {
      process_cmdline: 'SrcProcCmdLine',
      process_id: 'SrcProcPID',
      process_name: 'SrcProcName',
      process_path: 'SrcProcImagePath',
    },
    registry_event: {
      process_cmdline: 'SrcProcCmdLine',
      process_id: 'SrcProcPID',
      process_name: 'SrcProcName',
      process_path: 'SrcProcImagePath',
    },
    network_connection: {
      process_cmdline: 'SrcProcCmdLine',
      process_id: 'SrcProcPID',
      process_name: 'SrcProcName',
      process_path: 'SrcProcImagePath',
    },
    dns: {
      process_cmdline: 'SrcProcCmdLine',
      process_id: 'SrcProcPID',
      process_name: 'SrcProcName',
      process_path: 'SrcProcImagePath',
    },
    image_load: {
      sha256: 'src.process.image.sha256',
      process_cmdline: 'SrcProcCmdLine',
      process_id: 'SrcProcPID',
      process_name: 'SrcProcName',
      process_path: 'SrcProcImagePath',
    },
    pipe_created: {
      process_cmdline: 'SrcProcCmdLine',
      process_id: 'SrcProcPID',
      process_name: 'SrcProcName',
      process_path: 'SrcProcImagePath',
    },
  },
  'SentinelOne Singularity': {
    process_creation: {
      sha256: 'tgt.process.image.sha256',
      md5: 'tgt.process.image.md5',
    },  
    file_event: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    registry_event: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    image_load: {
      md5: 'module.md5',
      sha256: 'tgt.process.image.sha256',
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    network_connection: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    dns: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    pipe_created: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
  },
  'SentinelOne - PowerQuery': {
    process_creation: {
      sha256: 'tgt.process.image.sha256',
      md5: 'tgt.process.image.md5',
    },  
    file_event: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    registry_event: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    image_load: {
      md5: 'module.md5',
      sha256: 'tgt.process.image.sha256',
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    network_connection: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    dns: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
    pipe_created: {
      process_cmdline: 'src.process.cmdline',
      process_id: 'src.process.pid',
      process_name: 'src.process.name',
      process_path: 'src.process.image.path',
    },
  },
  'CarbonBlack Cloud - Investigate': {
    process_creation: {
      md5: 'hash',
      sha256: 'hash',
    },
    file_event: {
      md5: 'filemod_hash',
      sha256: 'filemod_hash',
    },
    image_load: {
      md5: 'modload_hash',
      sha256: 'modload_hash',
    },
  },
  'CarbonBlack Response': {
    file_event: {
      md5: 'filewrite_md5',
      sha256: 'filewrite_sha256',
    },
  },
  'Trend Micro Vision One': {
    process_creation: {
      md5: 'processFileHashMd5',
      sha256: 'processFileHashSha256',
    },
  },
  'Google SecOps': {
    file_event: { 
      process_name: 'parent_process_name',
      process_path: 'parent_process_path',
      process_id: 'parent_process_id',
    },
    network_connection: { 
      process_name: 'parent_process_name',
      process_path: 'parent_process_path',
      process_id: 'parent_process_id',
    },
    dns: { 
      process_name: 'parent_process_name',
      process_path: 'parent_process_path',
      process_id: 'parent_process_id',
    },
    image_load: { 
      process_name: 'parent_process_name',
      process_path: 'parent_process_path',
      process_id: 'parent_process_id',
    },
    registry_event: { 
      process_name: 'parent_process_name',
      process_path: 'parent_process_path',
      process_id: 'parent_process_id',
    }
  }
}
